
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    private handleReload = () => {
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            return (
                <Container component="main" maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            bgcolor: 'background.paper',
                        }}
                    >
                        <ErrorOutlineIcon color="error" sx={{ fontSize: 64, mb: 2 }} />

                        <Typography component="h1" variant="h4" gutterBottom>
                            Something went wrong
                        </Typography>

                        <Typography variant="body1" color="text.secondary" paragraph>
                            We encountered an unexpected error. Please try reloading the page.
                        </Typography>

                        {this.state.error && (
                            <Box
                                component="pre"
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    p: 2,
                                    bgcolor: 'grey.100',
                                    borderRadius: 1,
                                    overflow: 'auto',
                                    width: '100%',
                                    textAlign: 'left',
                                    fontSize: '0.875rem',
                                    color: 'error.main',
                                    maxHeight: '200px',
                                }}
                            >
                                {this.state.error.toString()}
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={this.handleReload}
                            sx={{ mt: 2 }}
                        >
                            Reload Page
                        </Button>
                    </Paper>
                </Container>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
